import React, { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Collapse, Typography } from '@mui/material';
import { CalendarTodayOutlined, ExpandMore } from '@mui/icons-material';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  defaultEventCategorySet,
  DefaultEventCategoryId,
  eventCategoryColorMap,
} from '@demind-inc/core';

import { ChatSuggestedEvent } from '../../../data-access';
import { getSuggestedBlockKey } from '../../../data-access/helpers';
import { formatTimeFromDayjs, formatTimeFromHHMM, isHHMMFormat } from '../../../helpers';
import { useGeneralSettings, useProjectFilter } from '../../../hooks';
import { ProjectFilterItem } from '../../../hooks';
import { ProjectSelector } from '../../Project';
import { getDefaultCategoryEventIconSet, PendingIndicatorDot } from '../../common';
import { AssistantChatSuggestedBlockCTA } from '../AssistantChatSuggestedBlockCTA';
import { isAppleRemindersDesktopSupported } from '../../../helpers/appleRemindersDesktop.helpers';
import { SuggestedBlockHandle, SuggestedBlockActionsVisibility } from '../SuggestedBlocks/types';
import SuggestedBlockActionsRow from '../SuggestedBlocks/SuggestedBlockActionsRow';
import { DescriptionBox } from '../DescriptionBox';
import { formatDurationLabel, getDisplayDateLabel } from './helpers';
import { getSuggestedBlockPalette } from './useSuggestedBlockPalette';
import { getDefaultEventCategoryTitle } from '../../DataView/i18n';
import './AssistantChatSuggestedBlocks.scss';

interface AssistantChatSuggestedTaskBlockProps {
  block: ChatSuggestedEvent;
  loadingEventId?: string;
  processedIds: string[];
  onProcessedIdsChange: (ids: string[]) => void;
  onManageTodo?: (e: ChatSuggestedEvent, board: ProjectFilterItem) => Promise<boolean>;
  /** 'screen' renders the grouped-card action row (Accept/Discard) instead
   * of the plain chat-feed CTA — used by SuggestedBlockRow/SuggestionGroupCard. */
  variant?: 'default' | 'screen';
  onDiscard?: (eventId: string) => void;
  actionsVisibility?: SuggestedBlockActionsVisibility;
  defaultExpanded?: boolean;
}

/** Dashed incomplete-circle glyph matching RN TaskBlock's Svg Circle header icon. */
const TaskCheckIcon: React.FC = () => (
  <svg
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden
    className="assistant-chat-suggested-blocks__task-check-icon"
  >
    <circle
      cx={10}
      cy={10}
      r={8}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeDasharray="1 3.5"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

/**
 * Suggested task card matching v4 Figma / RN AssistantChatSuggestedTaskBlock
 * (e.g. "Finish the Q4 deck") — Header (dashed circle + title, chevron),
 * collapsible Body (divider, date·time·duration meta, category + project
 * chips, detail), and the shared Discard + Accept row.
 */
const AssistantChatSuggestedTaskBlock = memo(
  forwardRef<SuggestedBlockHandle, AssistantChatSuggestedTaskBlockProps>(
    (
      {
        block,
        loadingEventId,
        processedIds,
        onProcessedIdsChange,
        onManageTodo = () => Promise.resolve(false),
        variant = 'default',
        onDiscard,
        actionsVisibility = 'visible',
        defaultExpanded = true,
      },
      ref
    ) => {
      const { t } = useTranslation();
      const {
        generalSettings: { timeFormat },
      } = useGeneralSettings();
      const { getDefaultProject, projectOptionsWithoutAll } = useProjectFilter();
      const isScreenVariant = variant === 'screen';
      const palette = useMemo(
        () => getSuggestedBlockPalette('task', isScreenVariant),
        [isScreenVariant]
      );
      const blockContainerStyle = {
        '--block-card-bg': palette.cardBackground,
        '--block-chip-bg': palette.chipBackground,
        '--block-time-bg': palette.timePillBackground,
        '--block-secondary-text': palette.secondaryText,
      } as React.CSSProperties;

      // Screen variant starts collapsed — matches RN isExpanded/handleToggleExpanded.
      const [isExpanded, setIsExpanded] = useState(defaultExpanded);
      const [selectedProject, setSelectedProject] = useState<ProjectFilterItem | null>(null);

      useEffect(() => {
        if (selectedProject) {
          return;
        }
        setSelectedProject(getDefaultProject());
      }, [getDefaultProject, selectedProject]);

      const blockKey = getSuggestedBlockKey(block);
      const isProcessed = processedIds.includes(blockKey);
      const isLoading = loadingEventId === blockKey;
      const isDeleteAction = block.action === 'deleteTask';
      const isPending = actionsVisibility === 'visible' || actionsVisibility === 'loading';

      const category = useMemo(
        () =>
          block.categoryId
            ? defaultEventCategorySet.find((cat) => cat.id === block.categoryId)
            : null,
        [block.categoryId]
      );

      const hasEndTime = (() => {
        if (!block.endTime) {
          return false;
        }
        // HHMM strings like "00:00" are invalid as bare dayjs() inputs — parse
        // them explicitly so midnight doesn't falsely count as "has end time".
        const end = isHHMMFormat(block.endTime)
          ? dayjs(`2000-01-01T${block.endTime}`)
          : dayjs(block.endTime);
        return end.isValid() && end.format('HH:mm') !== '00:00';
      })();

      const currentDateLabel = useMemo(
        () => getDisplayDateLabel(block.date, block.startTime),
        [block.date, block.startTime]
      );

      const durationLabel = useMemo(
        () => formatDurationLabel(block.durationMin),
        [block.durationMin]
      );

      const endTimeLabel = useMemo(() => {
        if (!hasEndTime || !block.endTime) {
          return null;
        }
        return isHHMMFormat(block.endTime)
          ? formatTimeFromHHMM(block.endTime, timeFormat, true)
          : formatTimeFromDayjs(dayjs(block.endTime), timeFormat, true);
      }, [block.endTime, hasEndTime, timeFormat]);

      const metaText = useMemo(
        () => [currentDateLabel, endTimeLabel, durationLabel].filter(Boolean).join(' · '),
        [currentDateLabel, endTimeLabel, durationLabel]
      );

      const buildTaskManageTitle = (b: ChatSuggestedEvent) => {
        if (isProcessed) {
          return t('task.ai.suggestedBlocks.done');
        }
        if (b.action === 'deleteTask') {
          return t('task.ai.suggestedBlocks.deleteTask', { defaultValue: 'Delete Task' });
        }
        return b.before
          ? t('task.ai.suggestedBlocks.updateTask', { defaultValue: 'Update Task' })
          : t('task.ai.suggestedBlocks.createTask', { defaultValue: 'Create Task' });
      };

      const handleClickTaskBlock = async (b: ChatSuggestedEvent): Promise<boolean> => {
        if (!selectedProject) {
          return false;
        }
        const result = await onManageTodo(b, selectedProject);
        if (result) {
          onProcessedIdsChange([...processedIds, getSuggestedBlockKey(b)]);
        }
        return result;
      };

      useImperativeHandle(
        ref,
        () => ({
          accept: () => handleClickTaskBlock(block),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [block, selectedProject]
      );

      const handleProjectSelect = (projectId: string) => {
        const project = projectOptionsWithoutAll.find((p) => p.todoTaskId === projectId);
        setSelectedProject(project || null);
      };

      const renderDefaultTimeDisplay = () => {
        const dueDate = getDisplayDateLabel(block.date, block.startTime);
        const beforeDueDate = getDisplayDateLabel(block.before?.date, block.before?.startTime);

        return (
          <>
            <div className="assistant-chat-suggested-blocks__datetime-wrapper">
              <div
                className={clsx(
                  'assistant-chat-suggested-blocks__time-item',
                  'assistant-chat-suggested-blocks__time-item--with-purple'
                )}
              >
                {beforeDueDate && beforeDueDate !== dueDate && (
                  <Typography
                    variant="subtitle2"
                    className="assistant-chat-suggested-blocks__date-chip"
                  >
                    {beforeDueDate}
                  </Typography>
                )}
                <Typography
                  variant="subtitle2"
                  className="assistant-chat-suggested-blocks__date-chip"
                >
                  {dueDate}
                </Typography>
              </div>
            </div>

            {hasEndTime && (
              <>
                <Typography variant="subtitle2">:</Typography>
                <div className="assistant-chat-suggested-blocks__time-item">
                  <div className="assistant-chat-suggested-blocks__before-wrapper">
                    {block.before?.endTime && (
                      <Typography
                        variant="subtitle2"
                        className="assistant-chat-suggested-blocks__time assistant-chat-suggested-blocks__before-text"
                      >
                        {formatTimeFromDayjs(dayjs(block.before?.endTime), timeFormat, true)}
                      </Typography>
                    )}
                    <Typography
                      variant="subtitle2"
                      className="assistant-chat-suggested-blocks__time"
                    >
                      {formatTimeFromDayjs(dayjs(block.endTime), timeFormat, true)}
                    </Typography>
                  </div>
                </div>
              </>
            )}
          </>
        );
      };

      const bodyContent = (
        <>
          {isScreenVariant && !!metaText && (
            <div className="assistant-chat-suggested-blocks__meta-row">
              <CalendarTodayOutlined
                className="assistant-chat-suggested-blocks__meta-icon"
                fontSize="inherit"
              />
              <Typography variant="body2" className="assistant-chat-suggested-blocks__meta-text">
                {metaText}
              </Typography>
            </div>
          )}

          {isScreenVariant ? (
            <div className="assistant-chat-suggested-blocks__meta-chips-row">
              {!!category && (
                <div className="assistant-chat-suggested-blocks__category-chip">
                  <span
                    className="assistant-chat-suggested-blocks__category-chip-dot"
                    style={{
                      backgroundColor:
                        eventCategoryColorMap[category.id as DefaultEventCategoryId] ??
                        'var(--color-primary, #680ddb)',
                    }}
                  />
                  <span>
                    {getDefaultEventCategoryTitle(
                      t,
                      category.id as DefaultEventCategoryId,
                      category.name
                    )}
                  </span>
                </div>
              )}
              {!isDeleteAction && (
                <div className="assistant-chat-suggested-blocks__project-selector-flex">
                  <ProjectSelector
                    projects={projectOptionsWithoutAll}
                    selectedProject={selectedProject}
                    onProjectSelect={handleProjectSelect}
                    enableToAdd={false}
                    isDisabledProject={(p) =>
                      p.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()
                    }
                    disabledProjectTooltip={
                      'Apple Reminders items can only be edited on mobile or the desktop app'
                    }
                    disabled={isProcessed}
                    className="assistant-chat-suggested-blocks__project-selector assistant-chat-suggested-blocks__project-selector--chip"
                  />
                </div>
              )}
            </div>
          ) : (
            !block.before && (
              <ProjectSelector
                projects={projectOptionsWithoutAll}
                selectedProject={selectedProject}
                onProjectSelect={handleProjectSelect}
                enableToAdd={false}
                isDisabledProject={(p) =>
                  p.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()
                }
                disabledProjectTooltip={
                  'Apple Reminders items can only be edited on mobile or the desktop app'
                }
                disabled={isProcessed || isDeleteAction}
                className="assistant-chat-suggested-blocks__project-selector"
              />
            )
          )}

          {!!block.description && <DescriptionBox description={block.description} />}

          {isScreenVariant ? (
            <SuggestedBlockActionsRow
              visibility={isLoading ? 'loading' : actionsVisibility}
              acceptLabel={buildTaskManageTitle(block)}
              acceptDisabled={!selectedProject || isProcessed}
              onAccept={() => handleClickTaskBlock(block)}
              onDiscard={() => onDiscard?.(block.eventId)}
            />
          ) : (
            <AssistantChatSuggestedBlockCTA
              title={buildTaskManageTitle(block)}
              loading={isLoading}
              type="task"
              onClick={() => handleClickTaskBlock(block)}
              disabled={isProcessed || !selectedProject}
              processed={isProcessed}
            />
          )}
        </>
      );

      return (
        <div
          className={clsx('assistant-chat-suggested-blocks__block-container', {
            'assistant-chat-suggested-blocks__block-container--screen': isScreenVariant,
            'assistant-chat-suggested-blocks__block-container--task': true,
          })}
          style={blockContainerStyle}
        >
          <div
            className={clsx('assistant-chat-suggested-blocks__block-row', {
              'assistant-chat-suggested-blocks__block-row--screen': isScreenVariant,
            })}
            onClick={isScreenVariant ? () => setIsExpanded((prev) => !prev) : undefined}
          >
            <div className="assistant-chat-suggested-blocks__before-wrapper assistant-chat-suggested-blocks__before-wrapper--flex">
              {block.before?.name && block.before.name !== block.name && (
                <Typography
                  variant="body2"
                  className="assistant-chat-suggested-blocks__title assistant-chat-suggested-blocks__before-text"
                >
                  {block.before.name}
                </Typography>
              )}
              <div className="assistant-chat-suggested-blocks__title-with-category">
                {isScreenVariant ? (
                  <span className="assistant-chat-suggested-blocks__header-icon-wrapper">
                    <TaskCheckIcon />
                  </span>
                ) : (
                  category && (
                    <span className="assistant-chat-suggested-blocks__category-item">
                      {getDefaultCategoryEventIconSet(
                        category.id as DefaultEventCategoryId,
                        12,
                        'var(--color-black, #131314)'
                      )}
                    </span>
                  )
                )}
                <Typography
                  variant="body2"
                  className="assistant-chat-suggested-blocks__title"
                  title={block.name}
                >
                  {block.name}
                </Typography>
              </div>
            </div>
            <div className="assistant-chat-suggested-blocks__datetime-wrapper">
              {isScreenVariant ? (
                <button
                  type="button"
                  className={clsx('assistant-chat-suggested-blocks__expand-toggle', {
                    'assistant-chat-suggested-blocks__expand-toggle--expanded': isExpanded,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded((prev) => !prev);
                  }}
                  aria-label={
                    isExpanded
                      ? t('task.ai.suggestedBlocks.collapse')
                      : t('task.ai.suggestedBlocks.expand')
                  }
                >
                  <ExpandMore fontSize="small" />
                </button>
              ) : (
                <>
                  <div className="assistant-chat-suggested-blocks__time-wrapper">
                    {renderDefaultTimeDisplay()}
                  </div>
                  <div
                    className={clsx(
                      'assistant-chat-suggested-blocks__time-wrapper',
                      'assistant-chat-suggested-blocks__time-item',
                      'assistant-chat-suggested-blocks__time-item--with-border'
                    )}
                  >
                    <Typography
                      variant="subtitle2"
                      className="assistant-chat-suggested-blocks__time"
                    >
                      {durationLabel ?? `${block.durationMin ?? 0}m`}
                    </Typography>
                  </div>
                </>
              )}
            </div>
          </div>

          <Collapse in={!isScreenVariant || isExpanded} timeout="auto" unmountOnExit>
            {bodyContent}
          </Collapse>
        </div>
      );
    }
  )
);

export default AssistantChatSuggestedTaskBlock;
